// Clientes Dashboard - Portal de facturación para clientes autenticados
import { useTranslation } from 'react-i18next';
import { ClientLayout } from '@/components/Layout/ClientLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { SUPPORT_EMAIL } from '@/config/constants';
import { Badge } from '@/components/ui/badge';
import { useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '@/providers/AuthProvider';
import { useClienteFichaQuery } from '@/hooks/useClienteExpediente';
import { hasCompletedClientOnboarding } from '@/pages/ClienteOnboarding';
import {
  DocumentTextIcon,
  CreditCardIcon,
  DocumentDuplicateIcon,
  UserCircleIcon,
  ClipboardDocumentListIcon,
  CalendarDaysIcon,
  ClockIcon,
  QuestionMarkCircleIcon,
} from '@heroicons/react/24/outline';

export function ClientesDashboard() {
  const navigate = useNavigate();
  const { t } = useTranslation('portal');
  const { user } = useAuth();
  const { data: ficha } = useClienteFichaQuery();

  // Redirect new clients to onboarding if not completed
  if (user?.role === 'client' && !hasCompletedClientOnboarding()) {
    return <Navigate to="/clientes/onboarding" replace />;
  }

  // Indicadores reales desde ficha
  const contractCount = ficha?.contract_mandates?.length ?? 0;
  const pendingPayments = ficha?.billing_payments?.filter(p => p.status === 'open' || p.status === 'pending').length ?? 0;
  const solicitudesPendientes = ficha?.solicitudes_pendientes_count ?? 0;
  const completitudPct = ficha?.expediente_workspace?.completitud_pct;

  const quickActions = [
    {
      title: t('dashboardCards.myProcess'),
      description: t('dashboardCards.myProcessDesc'),
      icon: ClipboardDocumentListIcon,
      href: '/clientes/tramite',
      color: 'bg-indigo-500',
      badge: completitudPct != null ? `${completitudPct}%` : undefined,
    },
    {
      title: t('dashboardCards.myContracts'),
      description: t('dashboardCards.myContractsDesc'),
      icon: DocumentTextIcon,
      href: '/clientes/contratos',
      color: 'bg-blue-500',
      badge: contractCount > 0 ? String(contractCount) : undefined,
    },
    {
      title: t('dashboardCards.subscriptions'),
      description: t('dashboardCards.subscriptionsDesc'),
      icon: CreditCardIcon,
      href: '/clientes/suscripciones',
      color: 'bg-green-500',
    },
    {
      title: t('dashboardCards.invoices'),
      description: t('dashboardCards.invoicesDesc'),
      icon: DocumentDuplicateIcon,
      href: '/clientes/facturas',
      color: 'bg-purple-500',
      badge: pendingPayments > 0 ? String(pendingPayments) : undefined,
      badgeVariant: 'destructive' as const,
    },
    {
      title: t('dashboardCards.appointments'),
      description: t('dashboardCards.appointmentsDesc'),
      icon: CalendarDaysIcon,
      href: '/clientes/citas',
      color: 'bg-teal-500',
      badge: solicitudesPendientes > 0 ? String(solicitudesPendientes) : undefined,
      badgeVariant: 'secondary' as const,
    },
    {
      title: t('dashboardCards.activity'),
      description: t('dashboardCards.activityDesc'),
      icon: ClockIcon,
      href: '/clientes/actividad',
      color: 'bg-amber-500',
    },
    {
      title: t('dashboardCards.myProfile'),
      description: t('dashboardCards.myProfileDesc'),
      icon: UserCircleIcon,
      href: '/clientes/perfil',
      color: 'bg-orange-500',
    },
    {
      title: t('dashboardCards.help'),
      description: t('dashboardCards.helpDesc'),
      icon: QuestionMarkCircleIcon,
      href: '/clientes/ayuda',
      color: 'bg-gray-500',
    },
  ];

  return (
    <ClientLayout>
      <div className="space-y-6">
        {/* Welcome section */}
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
            {user?.full_name
              ? t('dashboard.welcomeUser', { name: user.full_name, defaultValue: `Hola, ${user.full_name}` })
              : t('dashboard.welcome')}
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            {t('dashboard.subtitle')}
          </p>
        </div>

        {/* Quick actions */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {quickActions.map((action) => {
            const Icon = action.icon;
            return (
              <Card
                key={action.href}
                className="hover:shadow-lg transition-shadow cursor-pointer group"
                onClick={() => navigate(action.href)}
              >
                <CardContent className="pt-6">
                  <div className="flex items-start gap-4">
                    <div className={`${action.color} p-3 rounded-lg text-white group-hover:scale-110 transition-transform`}>
                      <Icon className="h-6 w-6" aria-hidden="true" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-1">{action.title}</h3>
                        {action.badge && (
                          <Badge variant={action.badgeVariant || 'secondary'} className="text-xs">
                            {action.badge}
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">{action.description}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Info cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardContent className="pt-6">
              <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-3">{t('dashboard.quickAccess')}</h3>
              <div className="space-y-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full justify-start"
                  onClick={() => navigate('/clientes/contratos')}
                >
                  <DocumentTextIcon className="h-4 w-4 mr-2" aria-hidden="true" />
                  {t('dashboard.viewAllContracts')}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full justify-start"
                  onClick={() => navigate('/clientes/suscripciones')}
                >
                  <CreditCardIcon className="h-4 w-4 mr-2" aria-hidden="true" />
                  {t('dashboard.manageSubscriptions')}
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-3">{t('dashboard.support')}</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                {t('dashboard.supportText')}
              </p>
              <div className="space-y-2 text-sm">
                <div>
                  <span className="text-gray-500 dark:text-gray-400">{t('dashboard.supportEmail')}</span>{' '}
                  <a href={`mailto:${SUPPORT_EMAIL}`} className="text-primary hover:underline">
                    {SUPPORT_EMAIL}
                  </a>
                </div>
                <div>
                  <span className="text-gray-500 dark:text-gray-400">{t('dashboard.supportSchedule')}</span>{' '}
                  <span className="text-gray-900 dark:text-gray-100">{t('dashboard.supportScheduleValue')}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </ClientLayout>
  );
}
